import React, { useEffect, useMemo, useCallback } from "react";
import "./NotificationList.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import NotificationRowSkeleton from "./NotificationRowSkeleton";
import Icon from "../../components/Icon/Icon";
import {
    getPaginatedNotifications,
    updateNotificationAsRead,
} from "../../Reducers/actions/notificationActions";
import { setTopNavbar } from "../../Reducers/actions/navbarActions";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import NotificationItem from "./NotificationItem";
import { notificationTypeMap, NOTIFICATION_LIMIT } from "./notificationTypes.jsx";

function NotificationList() {
    const dispatch = useDispatch();
    const [readNotificationIds, setReadNotificationIds] = React.useState(new Set());
    const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = React.useState(false);
    const notifications = useSelector((state) => state?.notifications?.notifications);
    const notificationsLoading = useSelector((state) => state?.notifications?.notificationsLoading);
    const navigate = useNavigate();

    const fetchData = useCallback((offset, limit) => {
        dispatch(getPaginatedNotifications(offset, limit));
    }, [dispatch]);

    const handleNotificationClick = useCallback((redirectUrl) => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [navigate]);

    const notificationItems = useMemo(() => {
        if (!notifications?.data?.length) return null;
        
        return notifications.data.map((notification) => {
            const typeProps = notificationTypeMap[notification.type];
            
            return (
                <NotificationItem
                    key={notification.id}
                    notification={notification}
                    typeProps={typeProps}
                    onNotificationClick={handleNotificationClick}
                />
            );
        });
    }, [notifications?.data, handleNotificationClick]);

    useInfiniteScroll(fetchData, NOTIFICATION_LIMIT, notifications?.totalCount || 0);

    useEffect(() => {
        dispatch(setTopNavbar({ title: "Notifications", showBackArrow: true }));
    }, [dispatch]);

    useEffect(() => {
        if (hasAttemptedInitialLoad || notificationsLoading) return;
        if (notifications?.data?.length) {
            setHasAttemptedInitialLoad(true);
            return;
        }

        setHasAttemptedInitialLoad(true);
        fetchData(0, NOTIFICATION_LIMIT);
    }, [fetchData, hasAttemptedInitialLoad, notificationsLoading, notifications?.data?.length]);

    useEffect(() => {
        if (notifications?.data?.length) {
            const newUnreadIds = notifications.data
                .filter((n) => !readNotificationIds.has(n.id) && !n.is_read)
                .map((n) => n.id);

            if (newUnreadIds.length > 0) {
                updateNotificationAsRead(newUnreadIds)
                    .then(() => {
                        setReadNotificationIds((prevIds) => {
                            const newSet = new Set(prevIds);
                            newUnreadIds.forEach(id => newSet.add(id));
                            return newSet;
                        });
                    })
                    .catch((error) => {
                        console.error('Failed to mark notifications as read:', error);
                    });
            }
        }
    }, [notifications?.data, readNotificationIds]);

    if ((!hasAttemptedInitialLoad || notificationsLoading) && !notifications?.data?.length) {
        return (
            <div className="notification-container px-16" role="status" aria-label="Loading notifications">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <NotificationRowSkeleton key={i} />
                ))}
            </div>
        );
    }

    if (!notifications?.data?.length) {
        return (
            <div className="notification-container">
                <div className="empty d-flex jcc aic f-column">
                    <Icon alias="empty-notifications" width={200} color="#ff0000" />
                    <h3 className="title mt-40 fw-bold fg-dark">All good for now</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="notification-container px-16">
            {notificationItems}
            <div className="spacer" />
        </div>
    );
}

export default NotificationList;
